import type { Level } from "./curriculum";
import { emptyLearnerProfile, today } from "./data";
import type { CurriculumPathway, LearnerProfileInput } from "../types";

/**
 * The first-run decisions, as plain functions.
 *
 * Kept apart from the screens so the parts that decide something — which
 * class an age lands in, what a mistyped address probably meant, what a child
 * is saved as — can be tested without rendering a wizard.
 */

/** The ages the picker offers. 11 means "eleven or older": Class 5 is the last level. */
export const AGE_CHOICES = [3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

/** Colours a child can be, in the order siblings are given them. */
export const CHILD_COLOURS = ["blue", "mint", "rose", "gold", "lavender", "green"] as const;

/** "Ages 6-7" / "Ages 6–7" -> 6. */
function youngestAge(level: Level): number | null {
  const match = level.subtitle?.match(/(\d+)\s*[-–]/);
  return match ? Number(match[1]) : null;
}

/**
 * The level a child of this age usually starts at.
 *
 * Read from each level's own "Ages 6-7" label rather than a table written
 * here, so a level re-banded in the database places children correctly
 * without a release. Younger than every band starts at the first level, older
 * than every band at the last. Only ever a suggestion: the parent sees it
 * pre-selected and can change it, because age alone does not decide where a
 * child is ready to start.
 */
export function levelForAge(age: number, levels: Level[]): Level | null {
  const ordered = [...levels].filter((level) => level.is_active !== false).sort((a, b) => a.sort_order - b.sort_order);
  if (ordered.length === 0) return null;

  const banded = ordered
    .map((level) => ({ level, from: youngestAge(level) }))
    .filter((entry): entry is { level: Level; from: number } => entry.from !== null);
  if (banded.length === ordered.length) {
    const fits = banded.filter((entry) => entry.from <= age);
    return fits.length ? fits[fits.length - 1].level : banded[0].level;
  }

  // Labels missing or unreadable: fall back to one level a year from three.
  const index = Math.min(ordered.length - 1, Math.max(0, Math.floor(age) - 3));
  return ordered[index];
}

/** Whole years old on `on`, or null for a date that is not one (or is in the future). */
export function ageOn(birthday: string, on: Date = new Date()): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthday);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const born = new Date(year, month - 1, day);
  if (born.getMonth() !== month - 1 || born.getTime() > on.getTime()) return null;
  let age = on.getFullYear() - year;
  if (on.getMonth() < month - 1 || (on.getMonth() === month - 1 && on.getDate() < day)) age -= 1;
  return age;
}

/**
 * The providers a family is most likely to mistype. Gmail alone is most of
 * them; the rest are the ones Pakistani and Gulf families actually use.
 */
const COMMON_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "live.com",
  "ymail.com",
  "msn.com",
  "aol.com",
  "protonmail.com",
];

/**
 * Real providers a couple of keystrokes from one above. Never "corrected":
 * mail.com is one edit from gmail.com and is somebody's actual address.
 */
const OTHER_REAL_DOMAINS = ["mail.com", "email.com", "me.com", "mac.com", "gmx.com", "zoho.com", "yandex.com", "proton.me"];

function distance(a: string, b: string): number {
  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let diagonal = previous[0];
    previous[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const above = previous[j];
      previous[j] = Math.min(above + 1, previous[j - 1] + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return previous[b.length];
}

/**
 * "ayesha@gmial.com" -> "ayesha@gmail.com"; null when nothing looks wrong.
 *
 * With no confirmation email, a mistyped address is never caught — the account
 * simply belongs to an address nobody reads, and a forgotten password can never
 * be reset. So the one moment to catch it is while it is being typed. Only ever
 * a suggestion the parent taps; nothing is changed for them.
 */
export function suggestEmail(email: string): string | null {
  const value = email.trim().toLowerCase();
  const at = value.lastIndexOf("@");
  if (at < 1 || at === value.length - 1) return null;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (COMMON_DOMAINS.includes(domain) || OTHER_REAL_DOMAINS.includes(domain)) return null;
  // "ayesha@gmail" — the dot-com forgotten entirely.
  if (!domain.includes(".") && COMMON_DOMAINS.includes(`${domain}.com`)) return `${local}@${domain}.com`;
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    const gap = distance(domain, candidate);
    if (gap < bestDistance) {
      best = candidate;
      bestDistance = gap;
    }
  }
  // Two edits covers "gmial", "gmai.com" and "gmail.con" without turning a
  // real company domain into a webmail one.
  return best && bestDistance > 0 && bestDistance <= 2 ? `${local}@${best}` : null;
}

/** One child as the wizard holds them, before anything is saved. */
export type ChildDraft = {
  key: string;
  name: string;
  /** From the age chips; ignored once a birthday is given. */
  age: number | null;
  /** YYYY-MM-DD, when the parent preferred to give one. */
  birthday: string | null;
  levelSlug: string | null;
  gender: "female" | "male" | null;
  colour: string;
};

export function newChild(index: number): ChildDraft {
  return {
    key: `child-${index}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    age: null,
    birthday: null,
    levelSlug: null,
    gender: null,
    colour: CHILD_COLOURS[index % CHILD_COLOURS.length],
  };
}

/** The age the wizard should place this child by: the birthday's, when there is one. */
export function draftAge(child: ChildDraft, on: Date = new Date()): number | null {
  return child.birthday ? ageOn(child.birthday, on) : child.age;
}

/** What is still missing before this child can be saved, or null when nothing is. */
export function childProblem(child: ChildDraft, levels: Level[]): "name" | "age" | null {
  if (!child.name.trim()) return "name";
  // With levels to choose from, a child needs one; the age is how it is chosen.
  if (levels.length > 0 && !child.levelSlug) return "age";
  return null;
}

/**
 * The learner row a finished draft becomes.
 *
 * Parent-led, because that is what a family signing up here is doing — an Al
 * Noor teacher is something they ask for later, per subject, from the
 * curriculum page. The pathway is the one they chose in step one. Everything
 * else starts from the same empty profile the full form uses.
 */
export function learnerInputFor(
  child: ChildDraft,
  pathway: CurriculumPathway,
  levels: Level[],
  on: Date = new Date(),
): LearnerProfileInput {
  const level = levels.find((candidate) => candidate.slug === child.levelSlug) ?? null;
  const age = draftAge(child, on);
  return {
    ...emptyLearnerProfile(),
    name: child.name.trim(),
    // The stage still drives the mastery map and the older activity pool, so it
    // comes from the level — or, with no levels at all, from the age.
    stage: level?.stage ?? ((age ?? 6) <= 5 ? "foundation" : "primary"),
    level_slug: level?.slug ?? null,
    level_started_on: level ? today(on) : null,
    // "" is the date field opened and left empty — not a date to store.
    date_of_birth: child.birthday || null,
    gender: child.gender,
    avatar_color: child.colour,
    learning_route: "parent_led",
    curriculum_pathway: pathway,
    preferred_delivery: null,
    supported_subjects: [],
    pathway_blocks: null,
  };
}
